import type { ReactNode, FC } from 'react';
import Grid from '@mui/material/Grid';
import {
  Box,
  Container,
  Typography,
  Stack,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { AppConfig } from '../../../core';

export function ContactSection() {
  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('md'));

  return (
    <Box id="contact-section" component="section">
      <Container
        maxWidth="xl"
        sx={{
          pt: { xs: 4, sm: 12 },
          pb: { xs: 8, sm: 16 },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: { xs: 3, sm: 3 },
        }}
      >
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={4}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 800,
                  color: palette.primary.main,
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  lineHeight: 1.2,
                  mb: 2,
                }}
              >
                If you have any questions
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: palette.text.primary,
                  fontSize: { xs: '1rem', md: '1.15rem' },
                }}
              >
                Join us as a Cultural Contributor to help keep living traditions
                alive so the next generation inherits something real.
              </Typography>
              <Stack spacing={2}>
                <ContactLink
                  icon={
                    <EmailIcon
                      fontSize={isMobile ? 'medium' : 'large'}
                      sx={{ color: '#fff' }}
                    />
                  }
                  text={`${AppConfig.appContactEmail}`}
                  color={palette.info.main}
                  href={`mailto:${AppConfig.appContactEmail}`}
                  isEmail
                  isMobile={isMobile}
                />
                <ContactLink
                  icon={
                    <LinkedInIcon
                      fontSize={isMobile ? 'medium' : 'large'}
                      sx={{ color: '#fff' }}
                    />
                  }
                  text="Reach out on LinkedIn"
                  color={palette.info.main}
                  href={`${AppConfig.socials.linkedIn}`}
                  isMobile={isMobile}
                />
                <ContactLink
                  icon={
                    <FacebookIcon
                      fontSize={isMobile ? 'medium' : 'large'}
                      sx={{ color: '#fff' }}
                    />
                  }
                  text="Reach out on Facebook"
                  color={palette.info.main}
                  href={`${AppConfig.socials.facebook}`}
                  isMobile={isMobile}
                />
                <ContactLink
                  icon={
                    <InstagramIcon
                      fontSize={isMobile ? 'medium' : 'large'}
                      sx={{ color: '#fff' }}
                    />
                  }
                  text="Join us on Instagram"
                  color={palette.info.main}
                  href={`${AppConfig.socials.instagram}`}
                  isMobile={isMobile}
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
const ContactLink: FC<{
  icon: ReactNode;
  text: string;
  color: string;
  href: string;
  isEmail?: boolean;
  isMobile: boolean;
}> = ({ icon, text, color, href, isEmail = false, isMobile }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={2}
    component={Link}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    underline="none"
    sx={{
      cursor: 'pointer',
      transition: 'transform 0.2s ease',
      '&:hover': {
        transform: 'translateX(8px)',
        '& .MuiTypography-root': { color: '#1A1D2B' },
      },
      '&:focus-visible': {
        outline: '3px solid rgba(66, 165, 245, 0.7)',
        outlineOffset: 2,
        borderRadius: 1,
      },
    }}
  >
    <Box
      sx={{
        width: isMobile ? 40 : 48,
        height: isMobile ? 40 : 48,
        borderRadius: '50%',
        bgcolor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="body1"
      sx={{
        fontWeight: 500,
        color: isEmail ? '#2E7D32' : '#3d5afe',
        textDecoration: isEmail ? 'underline' : 'none',
        textUnderlineOffset: '4px',
      }}
    >
      {text}
    </Typography>
  </Stack>
);
